export interface OwnerStation {
  id: string;
  name: string;
  region: string;
  lat: number;
  lon: number;
  installedMw: number;
  opCapMw: number;
  status: 'green' | 'amber' | 'red' | 'pre_launch';
  statusLabel: string;
  statusNote: string;
  todayGenMwh: number | null;
  todayRevenueUah: number;
  monthRevenueUah?: number;
  percentOfPotential: number | null;
  // Live-only optional fields (mock provides weather; API provides telemetry meta)
  todayForecastMwh?: number;
  todayRevenueSoFarUah?: number;
  isPreLaunch?: boolean;
  hasTelemetry?: boolean;
  nowPacKw?: number | null;
  commissioningDate?: string | null;
  reason?: string | null;
  insolaceWm2?: number;
  weatherIcon?: string;
  temperature?: number;
  cloudPct?: number;
}

// Centralised status → colour so every block (map, tiles, dots, drill-down)
// treats pre-launch (grey) consistently and never paints an unbuilt plant green.
export function ownerStatusColor(status: string): string {
  if (status === 'green') return '#22C55E';
  if (status === 'amber') return '#F59E0B';
  if (status === 'red') return '#D12229';
  if (status === 'pre_launch') return '#6B7280';
  return '#9CA3AF';
}

/** Canonical compact station label for tiles & lists: drop the "СЕС " prefix,
 * keep the brand suffix so every widget shows the same form ("Нікополь-Еліос",
 * not a mix of "Нікополь" / "Нікополь-Еліос"). Map pins keep the full name. */
export const shortStationName = (name: string): string => name.replace(/^СЕС\s+/, '').trim();

export interface OwnerIncident {
  severity: 'red' | 'amber';
  icon: string;
  station: string;
  description: string;
  impactUah: number;
  status: string;
  statusType: 'resolved' | 'ongoing';
  // live-only enrichment (Ф4 incidents)
  pct?: number | null;
  kind?: string;
  since?: string | null;
  endedAt?: string | null;
}

// Lost-revenue ratio data (Ф4) from /owner/dashboard — for the proportion charts.
export interface OwnerLostRevenue {
  todayUah: number;
  byStation: { name: string; uah: number; pct: number; sharePct: number }[];
  byCause: { name: string; uah: number; sharePct: number }[];
}

export const stations: OwnerStation[] = [
  {
    id: 'tabaky',
    name: 'СЕС Табакі',
    region: 'Одеська обл.',
    lat: 45.72,
    lon: 28.62,
    installedMw: 1.0,
    opCapMw: 1.0,
    status: 'green',
    statusLabel: 'Працює',
    statusNote: '',
    todayGenMwh: 4.1,
    todayRevenueUah: 15600,
    insolaceWm2: 820,
    weatherIcon: 'ri-sun-line',
    temperature: 28,
    cloudPct: 12,
    percentOfPotential: 92,
  },
  {
    id: 'bolgrad',
    name: 'СЕС Болград Еліос',
    region: 'Одеська обл.',
    lat: 45.69,
    lon: 28.61,
    installedMw: 4.9,
    opCapMw: 3.43,
    status: 'amber',
    statusLabel: 'Обмеження',
    statusNote: 'ОСР 30%',
    todayGenMwh: 12.4,
    todayRevenueUah: 47000,
    insolaceWm2: 840,
    weatherIcon: 'ri-sun-cloudy-line',
    temperature: 27,
    cloudPct: 22,
    percentOfPotential: 68,
  },
  {
    id: 'prometheus',
    name: 'СЕС Прометей ЕТГ',
    region: 'м. Дніпро',
    lat: 48.46,
    lon: 35.04,
    installedMw: 4.2,
    opCapMw: 4.2,
    status: 'green',
    statusLabel: 'Працює',
    statusNote: '',
    todayGenMwh: 18.7,
    todayRevenueUah: 71000,
    insolaceWm2: 790,
    weatherIcon: 'ri-sun-line',
    temperature: 26,
    cloudPct: 8,
    percentOfPotential: 87,
  },
  {
    id: 'nikopol',
    name: 'СЕС Нікополь-Еліос',
    region: 'м. Нікополь',
    lat: 47.57,
    lon: 34.39,
    installedMw: 12.1,
    opCapMw: 2.25,
    status: 'amber',
    statusLabel: 'Обмеження ОСР',
    statusNote: 'ОСР до 2250 кВт',
    todayGenMwh: 9.8,
    todayRevenueUah: 37000,
    insolaceWm2: 795,
    weatherIcon: 'ri-sun-line',
    temperature: 26,
    cloudPct: 10,
    percentOfPotential: 61,
  },
  {
    id: 'nikelios',
    name: 'СЕС Нікеліос',
    region: 'м. Дніпро',
    lat: 48.44,
    lon: 35.02,
    installedMw: 3.6,
    opCapMw: 0,
    status: 'red',
    statusLabel: 'Повне обмеження',
    statusNote: 'обмеження 100%',
    todayGenMwh: 0,
    todayRevenueUah: 0,
    insolaceWm2: 810,
    weatherIcon: 'ri-sun-line',
    temperature: 27,
    cloudPct: 5,
    percentOfPotential: 0,
  },
  {
    id: 'luchky',
    name: 'СЕС Лучки',
    region: 'Закарпатська обл.',
    lat: 48.4,
    lon: 22.57,
    installedMw: 16.0,
    opCapMw: 16.0,
    status: 'pre_launch',
    statusLabel: 'Не запущено',
    statusNote: 'запуск 01.07.2026',
    todayGenMwh: 0,
    todayRevenueUah: 0,
    percentOfPotential: 0,
    isPreLaunch: true,
  },
];

export const ownerKpis = {
  earnedMonth: 2450000,
  earnedToday: 187540,
  forecastTodayRevenue: 243000,
  forecastTodayMwh: 64.3,
  factNowMwh: 41.2,
};

export const incidents: OwnerIncident[] = [
  {
    severity: 'red',
    icon: 'ri-close-circle-line',
    station: 'Прометей ЕТГ',
    description: 'відключення 04.06 (приліт по гріду ДТЕК), втрачено ≈ 46 тис грн',
    impactUah: 46000,
    status: 'Усунено',
    statusType: 'resolved',
  },
  {
    severity: 'amber',
    icon: 'ri-error-warning-line',
    station: 'Болград Еліос',
    description: 'обмеження ОСР 30% з 23.05, не компенсується',
    impactUah: 0,
    status: 'Триває',
    statusType: 'ongoing',
  },
  {
    severity: 'amber',
    icon: 'ri-error-warning-line',
    station: 'Нікополь-Еліос',
    description: 'обмеження ОСР до 2250 кВт',
    impactUah: 0,
    status: 'Триває',
    statusType: 'ongoing',
  },
  {
    severity: 'red',
    icon: 'ri-close-circle-line',
    station: 'Нікеліос',
    description: 'повне обмеження (власні потреби)',
    impactUah: 0,
    status: 'Триває',
    statusType: 'ongoing',
  },
];

export const lostRevenue = {
  monthly: 312000,
  restrictionsPct: 78,
  downtimePct: 22,
};

export const secondaryMetrics = {
  imbalance: -18000,
  bessArbitrage: 94000,
};

export const financialStrip = {
  offtaker: 'Тепла',
  receivables: 503000,
  paymentDue: '10.07',
  contractNote: 'Контракт: РДН −3%',
};

export const forecastQuality = {
  current: 93,
  target: 90,
  sparklineData: [91, 94, 89, 93, 96, 92, 95, 90, 93, 97, 94, 91, 93, 95, 92, 89, 94, 96, 93, 90, 92, 95, 94, 91, 93, 96, 92, 94, 93, 95],
};

export const revenueDaily: { day: number; revenue: number }[] = [
  { day: 1, revenue: 0 },
  { day: 2, revenue: 42000 },
  { day: 3, revenue: 115000 },
  { day: 4, revenue: 203000 },
  { day: 5, revenue: 271000 },
  { day: 6, revenue: 356000 },
  { day: 7, revenue: 448000 },
  { day: 8, revenue: 521000 },
  { day: 9, revenue: 598000 },
  { day: 10, revenue: 687000 },
  { day: 11, revenue: 770000 },
  { day: 12, revenue: 832000 },
  { day: 13, revenue: 918000 },
  { day: 14, revenue: 992000 },
  { day: 15, revenue: 1058000 },
  { day: 16, revenue: 1121000 },
  { day: 17, revenue: 1194000 },
  { day: 18, revenue: 1250000 },
  { day: 19, revenue: 1317000 },
  { day: 20, revenue: 1385000 },
  { day: 21, revenue: 1432000 },
  { day: 22, revenue: 1490000 },
  { day: 23, revenue: 1558000 },
  { day: 24, revenue: 1612000 },
  { day: 25, revenue: 1680000 },
  { day: 26, revenue: 1745000 },
  { day: 27, revenue: 1803000 },
  { day: 28, revenue: 1870000 },
  { day: 29, revenue: 1926000 },
  { day: 30, revenue: 1987000 },
  { day: 31, revenue: 2050000 },
  { day: 32, revenue: 2110000 },
  { day: 33, revenue: 2170000 },
  { day: 34, revenue: 2220000 },
  { day: 35, revenue: 2280000 },
  { day: 36, revenue: 2335000 },
  { day: 37, revenue: 2380000 },
  { day: 38, revenue: 2420000 },
  { day: 39, revenue: 2445000 },
  { day: 40, revenue: 2450000 },
];

// Daily (non-cumulative) revenue for June calendar days 1-30
// Days 1-17: actual; days 18-30: forecast
export const dailyRevenue: { day: number; revenue: number; type: 'actual' | 'forecast' }[] = [
  { day: 1, revenue: 0, type: 'actual' },
  { day: 2, revenue: 42000, type: 'actual' },
  { day: 3, revenue: 73000, type: 'actual' },
  { day: 4, revenue: 88000, type: 'actual' },
  { day: 5, revenue: 68000, type: 'actual' },
  { day: 6, revenue: 85000, type: 'actual' },
  { day: 7, revenue: 92000, type: 'actual' },
  { day: 8, revenue: 73000, type: 'actual' },
  { day: 9, revenue: 77000, type: 'actual' },
  { day: 10, revenue: 89000, type: 'actual' },
  { day: 11, revenue: 83000, type: 'actual' },
  { day: 12, revenue: 62000, type: 'actual' },
  { day: 13, revenue: 86000, type: 'actual' },
  { day: 14, revenue: 74000, type: 'actual' },
  { day: 15, revenue: 66000, type: 'actual' },
  { day: 16, revenue: 63000, type: 'actual' },
  { day: 17, revenue: 73000, type: 'actual' },
  { day: 18, revenue: 78000, type: 'forecast' },
  { day: 19, revenue: 82000, type: 'forecast' },
  { day: 20, revenue: 71000, type: 'forecast' },
  { day: 21, revenue: 69000, type: 'forecast' },
  { day: 22, revenue: 84000, type: 'forecast' },
  { day: 23, revenue: 76000, type: 'forecast' },
  { day: 24, revenue: 80000, type: 'forecast' },
  { day: 25, revenue: 73000, type: 'forecast' },
  { day: 26, revenue: 77000, type: 'forecast' },
  { day: 27, revenue: 68000, type: 'forecast' },
  { day: 28, revenue: 85000, type: 'forecast' },
  { day: 29, revenue: 79000, type: 'forecast' },
  { day: 30, revenue: 72000, type: 'forecast' },
];

export const hourlyGeneration: { hour: string; forecast: number; actual: number }[] = [
  { hour: '00:00', forecast: 0, actual: 0 },
  { hour: '01:00', forecast: 0, actual: 0 },
  { hour: '02:00', forecast: 0, actual: 0 },
  { hour: '03:00', forecast: 0, actual: 0 },
  { hour: '04:00', forecast: 0.1, actual: 0 },
  { hour: '05:00', forecast: 1.2, actual: 0.8 },
  { hour: '06:00', forecast: 4.5, actual: 3.9 },
  { hour: '07:00', forecast: 8.2, actual: 7.1 },
  { hour: '08:00', forecast: 12.1, actual: 11.3 },
  { hour: '09:00', forecast: 14.8, actual: 13.9 },
  { hour: '10:00', forecast: 16.3, actual: 15.7 },
  { hour: '11:00', forecast: 17.1, actual: 16.4 },
  { hour: '12:00', forecast: 17.5, actual: 17.1 },
  { hour: '13:00', forecast: 17.8, actual: 16.9 },
  { hour: '14:00', forecast: 17.2, actual: 16.5 },
  { hour: '15:00', forecast: 15.9, actual: 15.1 },
  { hour: '16:00', forecast: 13.4, actual: 12.8 },
  { hour: '17:00', forecast: 10.2, actual: 9.8 },
  { hour: '18:00', forecast: 6.8, actual: 6.5 },
  { hour: '19:00', forecast: 3.2, actual: 3.1 },
  { hour: '20:00', forecast: 0.5, actual: 0.4 },
  { hour: '21:00', forecast: 0, actual: 0 },
  { hour: '22:00', forecast: 0, actual: 0 },
  { hour: '23:00', forecast: 0, actual: 0 },
];

export const inverters = [
  { id: 1, healthy: true },
  { id: 2, healthy: true },
  { id: 3, healthy: true },
  { id: 4, healthy: true },
  { id: 5, healthy: true },
  { id: 6, healthy: true },
  { id: 7, healthy: true },
  { id: 8, healthy: false },
];

export const formatMoney = (value: number): string => {
  if (value >= 1000000) {
    const mln = value / 1000000;
    if (mln % 1 === 0) return `${mln.toFixed(0)} млн грн`;
    return `${mln.toFixed(2)} млн грн`;
  }
  if (value >= 1000) {
    return `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} грн`;
  }
  return `${value} грн`;
};

export const formatMoneyShort = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)} млн`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)} тис`;
  return `${value}`;
};

export const formatEnergy = (value: number): string => {
  if (value === 0) return '0';
  if (value >= 1) return `${value.toFixed(1)} МВт·год`;
  return `${(value * 1000).toFixed(0)} кВт·год`;
};